import apiClient from '../../apis';

/**
 * The setup wizard's scans (#1622): `POST /api/builder/setup/scans` starts one, `GET …/scans/{id}` reads it. A scan
 * is its owner's alone; every refusal comes back as a code (`detail.code`), never as warehouse text. The shared
 * client's response interceptor (`apis.ts`) already unwraps the `{success, data}` envelope: `response.data` IS the scan.
 */

export type ScanState = 'running' | 'ready' | 'failed';

export interface ScanSchema { schema: string; listed: number; eligible: number; suggested: number }

export interface ScanSummary {
  listed: number;
  eligible: number;
  suggested?: number;
  flagged: number;
  schemas: ScanSchema[];
  schemas_listed: string[];
  ineligible: Record<string, number>;
  history: {
    available: boolean;
    reason?: string | null;
    window?: { kind: 'since_reset'; since_month: string | null } | { kind: 'days'; days: number } | null;
    coverage?: { rows_read: number; counted: number; unparsed: number; not_read_only?: number; ambiguous: number;
      other_catalog: number; cap_hit: boolean };
  };
}

export interface TableRow {
  schema: string;
  name: string;
  kind: 'table' | 'view';
  eligible: boolean;
  reason: string | null;
  rows: number | null;
  rows_estimated: boolean;
  queries: number | null;
  last_updated_month: string | null;
  flags: string[];
  suggested: boolean;
}

export interface TablePage { tables: TableRow[]; next_cursor: string | null }

export interface Resolved { tables: string[]; count: number; connection_id: string; scan_id: string }

export interface Scan {
  scan_id: string;
  connection_id: string;
  state: ScanState;
  reason_code: string | null;
  schemas: string[];
  summary: ScanSummary | null;
  created_at: string | null;
  finished_at: string | null;
}

export class SetupError extends Error {
  code: string;
  status: number;

  constructor(code: string, status: number) {
    super(code);
    this.name = 'SetupError';
    this.code = code;
    this.status = status;
  }
}

const refused = (e: any): never => {
  if (e instanceof SetupError) throw e;
  const status = e?.response?.status ?? 0;
  const detail = e?.response?.data?.detail;
  const code = typeof detail?.code === 'string' ? detail.code : status === 404 ? 'not_found' : status ? 'refused' : 'unreachable';
  throw new SetupError(code, status);
};

/** A scan as the interceptor hands it over; anything else is a refusal, never `undefined` passed on as a scan. */
function scanOf(data: any): Scan {
  if (!data || typeof data.scan_id !== 'string' || !['running', 'ready', 'failed'].includes(data.state)) throw new SetupError('bad_reply', 0);
  return data as Scan;
}

export const startScan = async (connectionId: string, schemas?: string[]): Promise<Scan> => {
  try {
    const { data } = await apiClient.post('/api/builder/setup/scans', { connection_id: connectionId, ...(schemas ? { schemas } : {}) });
    return scanOf(data);
  } catch (e) {
    return refused(e);
  }
};

export const getScan = async (scanId: string): Promise<Scan> => {
  try {
    const { data } = await apiClient.get(`/api/builder/setup/scans/${encodeURIComponent(scanId)}`);
    return scanOf(data);
  } catch (e) {
    return refused(e);
  }
};

/** One page of a ready scan's tables (at most `limit`, ≤ 200), searched on the server. */
export const listTables = async (scanId: string, params: { q?: string; cursor?: string | null; limit?: number }): Promise<TablePage> => {
  try {
    const { data } = await apiClient.get(`/api/builder/setup/scans/${encodeURIComponent(scanId)}/tables`, {
      params: { ...(params.q ? { q: params.q } : {}), ...(params.cursor ? { cursor: params.cursor } : {}), limit: params.limit ?? 50 } });
    if (!data || !Array.isArray(data.tables) || !('next_cursor' in data)) throw new SetupError('bad_reply', 0);
    return data as TablePage;
  } catch (e) {
    return refused(e);
  }
};

/** The selection rule resolved by the server into the tables a build takes. */
export const resolveSelection = async (scanId: string, rule: object): Promise<Resolved> => {
  try {
    const { data } = await apiClient.post(`/api/builder/setup/scans/${encodeURIComponent(scanId)}/resolve`, rule);
    if (!data || !Array.isArray(data.tables) || typeof data.count !== 'number') throw new SetupError('bad_reply', 0);
    return data as Resolved;
  } catch (e) {
    return refused(e);
  }
};

/** The warehouses setup knows; a live one is one Stroma can prove read-only. */
export const ENGINES: ReadonlyArray<{ id: string; label: string; live: boolean }> = [
  { id: 'postgres', label: 'Postgres', live: true },
  { id: 'bigquery', label: 'BigQuery', live: true },
  { id: 'snowflake', label: 'Snowflake', live: true },
  { id: 'databricks', label: 'Databricks', live: false },
  { id: 'redshift', label: 'Redshift', live: false },
];

/** Whether setup can build on this provider. One it can't (Trino) still builds through Studio New source. */
export const setupTakes = (provider: string) => ENGINES.some((e) => e.live && e.id === provider);
